import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUp, RotateCcw, X } from "lucide-react";
import RichText from "./RichText";
import { CHAT_STARTERS } from "../../data/content";

const TypingDots = () => (
    <div className="flex items-center gap-1.5 px-1" data-testid="chat-typing-indicator">
        {[0, 1, 2].map((i) => (
            <motion.span
                key={i}
                className="h-2 w-2 rounded-full bg-accent"
                animate={{ opacity: [0.25, 1, 0.25], y: [0, -3, 0] }}
                transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.16, ease: "easeInOut" }}
            />
        ))}
    </div>
);

const Bubble = ({ msg, onRetry }) => {
    const mine = msg.role === "user";
    return (
        <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
            <div
                data-testid={mine ? "chat-message-user" : "chat-message-assistant"}
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    mine
                        ? "rounded-br-sm bg-navy text-white"
                        : msg.error
                          ? "rounded-tl-sm border border-amber-200 bg-amber-50 text-navy"
                          : "rounded-tl-sm bg-mist text-navy"
                }`}
            >
                <RichText text={msg.text} />
                {msg.error && msg.retry && (
                    <button
                        type="button"
                        onClick={() => onRetry(msg.retry)}
                        data-testid="chat-retry-button"
                        className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-accent"
                    >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Send again
                    </button>
                )}
            </div>
        </div>
    );
};

const ChatPanel = ({ messages, busy, showStarters, onSend, onRetry, onClose }) => {
    const [value, setValue] = useState("");
    const endRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
    }, [messages, busy]);

    useEffect(() => {
        if (window.matchMedia("(min-width: 768px)").matches) inputRef.current?.focus();
    }, []);

    const submit = (e) => {
        e.preventDefault();
        if (!value.trim() || busy) return;
        onSend(value);
        setValue("");
    };

    return (
        <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_32px_80px_-24px_rgba(28,28,28,0.45)]">
            <header className="flex items-center gap-3 border-b border-white/10 bg-navy px-4 py-3.5">
                <img src="/logo-on-dark.png" alt="ChatProz" className="h-6 w-auto shrink-0 select-none md:h-7" />
                <div className="min-w-0 flex-1">
                    <p className="font-display text-sm font-semibold leading-tight text-white whitespace-nowrap">
                        ChatProz Assistant
                    </p>
                    <p className="flex items-center gap-1.5 text-[11px] text-white/55 whitespace-nowrap">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                        Answers questions, books calls
                    </p>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="chat-close-button"
                    aria-label="Close chat"
                    className="rounded-full p-1.5 text-white/60 transition-colors duration-200 hover:bg-white/10 hover:text-white"
                >
                    <X className="h-5 w-5" />
                </button>
            </header>

            <div
                data-lenis-prevent
                data-testid="chat-message-list"
                className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-4"
            >
                {messages.map((m) => (
                    <Bubble key={m.id} msg={m} onRetry={onRetry} />
                ))}
                {busy && <TypingDots />}
                {showStarters && (
                    <div className="space-y-2 pt-1">
                        {CHAT_STARTERS.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => onSend(s)}
                                data-testid={`chat-starter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
                                className="block w-full rounded-full border border-slate-200 px-4 py-2.5 text-left text-sm font-medium text-navy transition-colors duration-200 hover:border-accent hover:bg-mist"
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                )}
                <div ref={endRef} />
            </div>

            <form onSubmit={submit} className="border-t border-slate-200 px-3 py-3" data-testid="chat-form">
                <div className="flex items-end gap-2">
                    <textarea
                        ref={inputRef}
                        rows={1}
                        value={value}
                        maxLength={2000}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) submit(e);
                        }}
                        placeholder="Type your message"
                        data-testid="chat-input"
                        className="max-h-28 min-h-[44px] flex-1 resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:border-accent focus:outline-none"
                    />
                    <motion.button
                        type="submit"
                        disabled={busy || !value.trim()}
                        whileTap={{ scale: 0.92 }}
                        data-testid="chat-send-button"
                        aria-label="Send message"
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-white transition-colors duration-200 hover:bg-navy disabled:opacity-40"
                    >
                        <ArrowUp className="h-5 w-5" />
                    </motion.button>
                </div>
                <p className="mt-2 text-center text-[11px] text-slate-400">AI assistant by ChatProz</p>
            </form>
        </div>
    );
};

export default ChatPanel;
